import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { toast } from '../../store/toastStore';
import { isValidIsraeliId, normalizeIsraeliId } from '../../utils/israeliId';

/**
 * D-306 - "הפרופיל שלי": the employee's own ID number and secondary phone. Seen and edited by the
 * employee and the OTE manager only (the server enforces it). Rendered only for an OTE employee:
 * for anyone else the employee record is refused (403/404) and nothing shows.
 */
export default function EmployeePrivateDetails() {
  const { t } = useTranslation();
  const uid = useId();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  // DQ-108: only an OTE employee has an employee record - anyone else got a 403 in the console on every visit.
  const isOteEmployee = useAuthStore((s) => s.user?.is_ote_employee === true);
  const { data } = useQuery({
    queryKey: ['employee-profiles', 'mine', userId],
    queryFn: () => api.getEmployeeProfileByUser(userId!),
    enabled: !!userId && isOteEmployee,
    retry: false,
  });
  const profile = data?.data;
  const [idNumber, setIdNumber] = useState('');
  const [phone2, setPhone2] = useState('');
  const [idError, setIdError] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (profile) {
      setIdNumber(profile.id_number || '');
      setPhone2(profile.phone_secondary || '');
    }
  }, [profile]);

  const save = useMutation({
    mutationFn: () => api.updateEmployeeProfile(profile!.id, {
      id_number: idNumber.trim() === '' ? null : normalizeIsraeliId(idNumber),
      phone_secondary: phone2.trim() === '' ? null : phone2.trim(),
    }),
    onSuccess: () => {
      setServerError('');
      void queryClient.invalidateQueries({ queryKey: ['employee-profiles'] });
      toast('success', t('profile.privateSaved'));
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setServerError(e?.response?.data?.error?.message || t('common.error'));
    },
  });

  if (!profile) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (idNumber.trim() && !isValidIsraeliId(idNumber)) { setIdError(true); return; }
    setIdError(false);
    save.mutate();
  };

  return (
    <section className="py-6" data-testid="employee-private-details">
      <div className="mb-4 min-w-0">
        <h2 className="text-base font-semibold text-surface-900">{t('profile.privateDetails')}</h2>
        <p className="mt-0.5 text-sm text-surface-600">{t('profile.privateDetailsHint')}</p>
      </div>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor={`${uid}-id`} className="label">{t('employeeProfiles.idNumber')}</label>
            <input id={`${uid}-id`} className={`input text-start ${idError ? 'input-error' : ''}`} dir="ltr" inputMode="numeric" maxLength={9}
              value={idNumber} aria-invalid={idError} autoComplete="off"
              onChange={(e) => { setIdNumber(e.target.value.replace(/\D/g, '')); setIdError(false); setServerError(''); }} />
            {idError && <p role="alert" className="mt-1 text-xs text-danger-700">{t('employeeProfiles.idNumberInvalid')}</p>}
          </div>
          <div>
            <label htmlFor={`${uid}-p2`} className="label">{t('employeeProfiles.phoneSecondary')}</label>
            <input id={`${uid}-p2`} className="input text-start" dir="ltr" type="tel" maxLength={40} value={phone2}
              onChange={(e) => { setPhone2(e.target.value); setServerError(''); }} />
          </div>
        </div>
        {serverError && <p role="alert" className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">{serverError}</p>}
        <div className="flex justify-end">
          <button type="submit" disabled={save.isPending} className="btn btn-primary">
            {save.isPending ? t('common.updating') : t('profile.updateProfile')}
          </button>
        </div>
      </form>
    </section>
  );
}
